import { QuestionnaireAPI } from '../../services/api';
import StartView from './StartView';
import ReferencesView from './ReferencesView';
import QuestionView from './QuestionView';
import ChapterEndView from './ChapterEndView';
import TestEndView from './TestEndView';
import FinishView from './FinishView';
import PausedView from './PausedView';
import { CircularProgress, Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import StopDialog from '../pages/Questionnaire/QuestionnerRenders/StopDialog';
import TopNav from '../pages/PageUtils/User/TopNav/TopNav';
import WebcamRender from '../pages/PageUtils/User/Webcam';
import HeadLine from '../pages/PageUtils/User/TestHeadLine';
import { useState, useRef, useEffect, useContext } from 'react';
import { WebSocketContext } from '../../services/ClientWebSocketService';

const POPUP_CURSOR_KEY = 'lastOperatorPopupId';

/** Popup ids are clock-seeded on the server, so a stale cursor can never hide a new popup. */
const readStoredPopupCursor = () => {
  try {
    return Number(window.localStorage.getItem(POPUP_CURSOR_KEY)) || 0;
  } catch (e) {
    return 0; // private mode / storage disabled
  }
};

const storePopupCursor = (id) => {
  try {
    window.localStorage.setItem(POPUP_CURSOR_KEY, String(id));
  } catch (e) {
    // Non-fatal: the cursor still lives in memory for this page load.
  }
};


export default function QuestionnairePage() {
  const webSocket = useContext(WebSocketContext);
  const [stateData, setStateData] = useState(null);
  const stateDataRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(loading);
  const [error, setError] = useState(null);
  const [openChapterEndedDialog, setOpenChapterEndedDialog] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  /// Whether the error dialog should have a refresh page button:
  const [errorDialogRefresh, setErrorDialogRefresh] = useState(false);
  // Operator popups are queued: they can arrive over the WebSocket and over the HTTP
  // heartbeat, and several can pile up while the test-taker is away.
  const [popupQueue, setPopupQueue] = useState([]);
  const seenPopupIds = useRef(new Set());
  // Cursor of the newest popup already dismissed. The server keeps re-sending anything
  // newer, so an undismissed popup survives a reload; persisting it means a reload
  // doesn't replay popups that were already read.
  const lastPopupId = useRef(null);
  if (lastPopupId.current === null) lastPopupId.current = readStoredPopupCursor();
  // Pending targetPosition, used when confirmation dialog is needed
  const pendingTargetPosition = useRef(null);
  // QuestionView registers a way to save the answer in progress, so an operator move
  // arriving mid-question doesn't discard it.
  const flushAnswer = useRef(null);

  // Add a popup to the queue, ignoring one already shown or already dismissed. The
  // server re-sends undismissed popups on every beat, so this runs constantly.
  // Returns true only the first time a given popup is seen.
  const enqueuePopup = (popup) => {
    if (!popup) return false;
    if (typeof popup.id === "number") {
      if (popup.id <= lastPopupId.current) return false;
      if (seenPopupIds.current.has(popup.id)) return false;
      seenPopupIds.current.add(popup.id);
    }
    setPopupQueue((prev) => [...prev, { id: popup.id, message: popup.message || "" }]);
    return true;
  };

  // Dismiss the popup on screen and move the cursor past it, so the server stops
  // re-sending it. popupQueue[0] is what the dialog is showing.
  const dismissPopup = () => {
    const dismissed = popupQueue[0];
    if (dismissed && typeof dismissed.id === "number" && dismissed.id > lastPopupId.current) {
      lastPopupId.current = dismissed.id;
      storePopupCursor(dismissed.id);
    }
    setPopupQueue((prev) => prev.slice(1));
  };

  useEffect(() => {
    stateDataRef.current = stateData;
  }, [stateData]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Runs the given function `fn` if not currently loading. Sets loading state during the function execution.
  const guardLoading = async (fn) => {
    if (loadingRef.current) {
      console.log(`guardLoading: Skipping fn call because loading is true`);
      return;
    }

    setLoading(true);
    try {
      return await fn();
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  // Return a wrapper function that only runs the given function `fn` if not currently loading. Sets loading state during the function execution.
  const wrapGuardLoading = (fn) => async (...args) => {
    return guardLoading(async () => await fn(...args));
  };

  const arePositionsEqual = (pos1, pos2) => {
    if (!pos1 || !pos2) return pos1 === pos2;
    return (
      pos1.state === pos2.state &&
      pos1.testId === pos2.testId &&
      pos1.chapterId === pos2.chapterId &&
      pos1.questionIndex === pos2.questionIndex
    );
  };

  const fetchState = async ({ checkMismatch = false } = {}) => {
    try {
      const data = await QuestionnaireAPI.state();
      const currentStateData = stateDataRef.current;
      if (
        checkMismatch &&
        !loadingRef.current &&
        currentStateData?.questionnairePosition &&
        !arePositionsEqual(currentStateData.questionnairePosition, data.questionnairePosition)
      ) {
        setErrorDialogMessage("מיקומך בשאלון השתנה. אנא רפרש/י את העמוד.");
        setErrorDialogRefresh(true);
        setErrorDialogOpen(true);
        return;
      }
      setStateData(data);
      setTimeLeft(data.payload.timeLeft ?? null);
    } catch (err) {
      console.error("Failed to fetch state:", err);
      setError(err.message || "Failed to load questionnaire.");
    }
  };

  /**
   * Follow a move the operator made: save whatever the test-taker had answered, then
   * re-render at the new position. No confirmation — the operator already decided.
   */
  const followOperatorMove = async () => {
    try {
      if (flushAnswer.current) await flushAnswer.current();
    } catch (err) {
      console.error("Could not save the answer before being moved:", err);
    }
    await fetchState();
  };

  // The heartbeat and WebSocket effects subscribe once and must not re-subscribe on every
  // render, so they call the latest version through a ref instead of listing it as a dep.
  const followOperatorMoveRef = useRef(null);
  useEffect(() => {
    followOperatorMoveRef.current = followOperatorMove;
  });

  // Initial fetch
  useEffect(() => {
    const loadInitialState = async () => {
      setLoading(true);
      try {
        await fetchState();
      } finally {
        setLoading(false);
      }
    };
    loadInitialState();
  }, []);

  // Re-fetch every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchState({ checkMismatch: true });
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timeLeft !== null && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeLeft]);

  // WebSocket listener
  useEffect(() => {
    if (webSocket) {
      const handleStateChange = () => {
        console.log("WebSocket message received, fetching state...");
        fetchState();
      };
      const handleOperatorPopup = (data) => {
        enqueuePopup({ id: data.popupId, message: data.popupMessage });
      };
      const handlePositionChanged = () => followOperatorMoveRef.current?.();
      webSocket.bindMessage("Go To Toilet", handleStateChange);
      webSocket.bindMessage("Return From Toilet", handleStateChange);
      webSocket.bindMessage("Operator Popup", handleOperatorPopup);
      webSocket.bindMessage("Position Changed", handlePositionChanged);
      return () => {
        webSocket.unbindMessage("Go To Toilet", handleStateChange);
        webSocket.unbindMessage("Return From Toilet", handleStateChange);
        webSocket.unbindMessage("Operator Popup", handleOperatorPopup);
        webSocket.unbindMessage("Position Changed", handlePositionChanged);
      };
    }
  }, [webSocket]);

  // Heartbeat: keeps the operator's online indicator accurate and picks up operator
  // popups even when the per-user WebSocket never managed to connect.
  useEffect(() => {
    let cancelled = false;
    const beat = async () => {
      const { popups, position } = await QuestionnaireAPI.heartbeat(lastPopupId.current);
      if (cancelled) return;
      // `.map` not `.forEach`: a popup arriving for the first time means the operator just
      // acted on this test-taker.
      const newPopup = popups.map(enqueuePopup).some(Boolean);

      // The operator moved this test-taker: follow along instead of leaving them on a
      // stale question until they happen to press "next".
      const shown = stateDataRef.current?.questionnairePosition;
      const moved = position && shown && !arePositionsEqual(shown, position);

      // A new popup without a move usually means time was granted where they already are
      // (an operator rescuing someone stuck on a spent timer). Nothing about the position
      // changed, so only the popup reveals it — re-read state rather than leaving a stale
      // clock on screen.
      if (!loadingRef.current && (moved || newPopup)) {
        followOperatorMoveRef.current?.();
      }
    };
    beat();
    const interval = setInterval(beat, 5 * 1000);

    // Browsers throttle timers in background tabs to roughly once a minute, so beat
    // as soon as the page is looked at again instead of waiting for the next tick.
    const beatIfVisible = () => {
      if (document.visibilityState === "visible") beat();
    };
    document.addEventListener("visibilitychange", beatIfVisible);
    window.addEventListener("focus", beatIfVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", beatIfVisible);
      window.removeEventListener("focus", beatIfVisible);
    };
  }, []);

  const handleNext = wrapGuardLoading(async (targetPosition = null, confirm = false) => {
    try {
      await QuestionnaireAPI.next(
        stateData.questionnairePosition,
        targetPosition,
        confirm
      );
      // Successfully moved
      setOpenChapterEndedDialog(false);
      pendingTargetPosition.current = null;
      await fetchState();
    } catch (err) {
      const errorCode = err.response && err.response.data && err.response.data.code;
      if (errorCode === 'CONFIRMATION_REQUIRED') {
        // Show StopDialog (or custom confirmation)
        setOpenChapterEndedDialog(true);
        pendingTargetPosition.current = targetPosition; // Save intended action
      } else if (errorCode === 'OVERRIDE_SET') {
        setErrorDialogMessage(".הועברת לפרק אחר ולכן לא ניתן לקפוץ לשאלות אחרות בפרק. ענה/י על השאלה הנוכחית ולאחר מכן ולחץ/י על הבא");
        setErrorDialogOpen(true);
      } else if (errorCode === 'CHAPTER_TIME_UP') {
        setErrorDialogMessage(".נגמר הזמן לפרק זה, לכן לא ניתן לקפוץ לשאלות אחרות בפרק. ענה/י על השאלה הנוכחית ולאחר מכן ולחץ/י על הבא");
        setErrorDialogOpen(true);
      } else if (errorCode === 'JUMP_OUT_OF_SCOPE') {
        setErrorDialogMessage(".פעולה זו אינה מורשית (שגיאת ניווט)");
        setErrorDialogOpen(true);
      } else if (errorCode === "POSITION_MISMATCH") {
        setErrorDialogMessage("מיקומך בשאלון השתנה. אנא רפרש/י את העמוד.");
        setErrorDialogRefresh(true);
        setErrorDialogOpen(true);
      } else {
        console.error("Next failed:", err);
        setErrorDialogMessage("אירעה שגיאה, אנא נסה/י שוב.\n" + (err?.response?.data?.error || ""));
        setErrorDialogRefresh(true);
        setErrorDialogOpen(true);
      }
    }
  });

  const handleConfirmNext = () => {
    handleNext(pendingTargetPosition.current, true);
  };

  const handleResume = wrapGuardLoading(async () => {
    try {
      await QuestionnaireAPI.resume();
      await fetchState();
    } catch (err) {
      console.error(err);
    }
  });

  const jumpToQuestionIndex = (index) => {
    const targetPos = {
      ...stateData.questionnairePosition,
      questionIndex: index
    };
    handleNext(targetPos);
  };

  const { state, payload, questionnairePosition } = stateData || {};

  const shouldRenderWebcam = state != 'PAUSED' && !(payload?.chapterIsBreak);

  return (
    <div>
      <TopNav
        title={`מבחן ${payload?.questionnaireName ?? "מיון"}`}
        shouldAddAskToilet={true}
        shouldAddRaiseHand={true}
        maxQuestionIndex={payload?.maxQuestionIndex}
        firstQuestionIndex={0}
        jumpToQuestions={jumpToQuestionIndex}
      />

      <Box sx={{ position: 'relative', width: '100%' }}>
        {shouldRenderWebcam ? (
          <WebcamRender />
        ) : (
          <div className="webcam-container">
            <div className="webcam-wrapper inactive">
              <div className="webcam-inactive-message">
                המצלמה תופעל מחדש עם המשך המבחן
              </div>
            </div>
          </div>
        )}

        {payload && payload.testName && <HeadLine text={payload.testName} /> || <br />}

        <StopDialog
          openDialog={openChapterEndedDialog}
          handleDialogClose={(shouldJump) => {
            if (shouldJump) handleConfirmNext();
            else setOpenChapterEndedDialog(false);
          }}
          stopMessageBeforeChapter="הפרק נגמר, במעבר לפרק הבא לא ניתן לשנות את התשובות של הפרק"
        />

        <Dialog
          open={errorDialogOpen}
          onClose={() => {
            if (!errorDialogRefresh) setErrorDialogOpen(false);
          }}
          PaperProps={{ dir: 'rtl' }}
          dir="rtl"
        >
          <DialogTitle>הודעה</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ textAlign: 'right', direction: 'rtl', whiteSpace: 'pre-line' }}>
              {errorDialogMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            {errorDialogRefresh ? (
              <Button onClick={() => window.location.reload()} color="primary" variant="contained">
                רענן דף
              </Button>
            ) : (
              <Button
                onClick={() => {
                  setErrorDialogOpen(false);
                  setErrorDialogRefresh(false);
                }}
                color="primary"
              >
                סגור
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Operator popup — non-blocking, test continues. Shows one popup at a time. */}
        <Dialog
          open={popupQueue.length > 0}
          onClose={dismissPopup}
          PaperProps={{ dir: 'rtl' }}
          dir="rtl"
        >
          <DialogTitle>
            הודעה מהמפעיל
            {popupQueue.length > 1 && ` (1 מתוך ${popupQueue.length})`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText style={{ textAlign: 'right', direction: 'rtl', whiteSpace: 'pre-line' }}>
              {popupQueue[0]?.message}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={dismissPopup} color="primary" variant="contained">
              סגור
            </Button>
          </DialogActions>
        </Dialog>

        {/* Main Content Render */}
        {(() => {
          if (error) {
            return (
              <Box p={4} textAlign="center">
                <Typography color="error" gutterBottom>{error}</Typography>
                <Button
                  onClick={() => window.location.reload()}
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                >
                  רענן דף
                </Button>
              </Box>
            );
          }

          if (loading && !stateData) {
            return (
              <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                <CircularProgress />
              </Box>
            );
          }

          switch (state) {
            case 'START':
              return <StartView payload={payload} onNext={() => handleNext(null, true)} />;
            case 'REFERENCES':
              return <ReferencesView
                payload={payload}
                timeLeft={timeLeft}
                onNext={(targetPos, confirm) => handleNext(targetPos, confirm)}
              />;
            case 'QUESTION':
              return <QuestionView
                payload={payload}
                timeLeft={timeLeft}
                moveToNextQuestion={handleNext}
                jumpToQuestionIndex={jumpToQuestionIndex}
                questionnairePosition={questionnairePosition}
                registerAnswerFlush={(fn) => { flushAnswer.current = fn; }}
              />;
            case 'CHAPTER_END':
              return <ChapterEndView payload={payload} onNext={() => handleNext(null, true)} />;
            case 'TEST_END':
              return <TestEndView payload={payload} onNext={() => handleNext(null, true)} />;
            case 'FINISH':
              return <FinishView payload={payload} />;
            case 'PAUSED':
              return <PausedView payload={payload} onResume={handleResume} />;
            default:
              return <Typography>Unknown State: {state}</Typography>;
          }
        })()}
      </Box>

    </div>
  );
}
